import {
    IxuFacebook,
    IxuLinkedIn,
    IxuMail,
    IxuPinterest,
    IxuTwitter,
} from '@issuu/ixu-react-icons';
import {
    shareLinkOnEmail,
    shareLinkOnFacebook,
    shareLinkOnLinkedIn,
    shareLinkOnPinterest,
    shareLinkOnTwitter,
    sharePublicationOnEmail,
    sharePublicationOnFacebook,
    sharePublicationOnLinkedIn,
    sharePublicationOnPinterest,
    sharePublicationOnTwitter,
} from '@issuu/social-micro';
import React, { useContext } from 'react';
import styled from 'styled-components';

import DocumentContext from '../DocumentPage/DocumentContext';
import type { ShareType } from './SelectShareType';

const SocialIconsRoot = styled.div`
    display: flex;
    justify-content: space-between;
`;

type Service = 'facebook' | 'linkedin' | 'pinterest' | 'twitter' | 'email';

type Props = {
    shareType: ShareType;
    shareLink: string;
    givenPageNumber: number;
};

export const ShareboxSocials: React.FC<Props> = ({
    givenPageNumber,
    shareType,
    shareLink,
}: Props) => {
    const {
        document: { revisionId, publicationId, documentName, description, title },
        hosts: { main, multimedia },
        publisher,
    } = useContext(DocumentContext);

    const config = {
        hosts: { main, multimedia },
    };

    const publication = {
        username: publisher.username,
        name: documentName,
        description,
        title,
    };

    const link = {
        url: shareLink,
        title,
        description,
    };

    const getShareMethod = (service: Service, shareType: ShareType) => {
        const sharePageNumber = shareType === 'page' ? givenPageNumber : undefined;
        const shouldShareByPublication = ['cover', 'page'].includes(shareType);
        switch (service) {
            case 'facebook':
                return shouldShareByPublication
                    ? sharePublicationOnFacebook(config, publication, sharePageNumber)
                    : shareLinkOnFacebook(link);
            case 'linkedin':
                return shouldShareByPublication
                    ? sharePublicationOnLinkedIn(config, publication, sharePageNumber)
                    : shareLinkOnLinkedIn(link);
            case 'twitter':
                return shouldShareByPublication
                    ? sharePublicationOnTwitter(config, publication, sharePageNumber)
                    : shareLinkOnTwitter(config, link);
            case 'email':
                return shouldShareByPublication
                    ? sharePublicationOnEmail(config, publication, sharePageNumber)
                    : shareLinkOnEmail(link);
            case 'pinterest':
                const imageUrl = `//${multimedia}/document/${revisionId}-${publicationId}/${givenPageNumber}/spread.jpg`;
                return shouldShareByPublication
                    ? sharePublicationOnPinterest(config, publication, sharePageNumber)
                    : shareLinkOnPinterest({ ...link, imageUrl });
            default:
                throw new Error(`Unsupported service: "${service}"`);
        }
    };

    return (
        <SocialIconsRoot>
            <IxuFacebook onClick={() => getShareMethod('facebook', shareType)} />
            <IxuLinkedIn onClick={() => getShareMethod('linkedin', shareType)} />
            <IxuPinterest onClick={() => getShareMethod('pinterest', shareType)} />
            <IxuTwitter onClick={() => getShareMethod('twitter', shareType)} />
            <IxuMail onClick={() => getShareMethod('email', shareType)} />
        </SocialIconsRoot>
    );
};
